/**
 * DemocratCharts — useQuotes.
 *
 * Fetches the latest 1d candles for a set of (symbol, exchange) pairs to
 * derive the current price and day-change. Cache-hit fast path means
 * subsequent calls are ~50ms each.
 *
 * Phase 5.3+ will add a dedicated /api/quotes endpoint so we don't have
 * to fetch 365 candles per symbol just to read the last two.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";

export interface Quote {
  symbol: string;
  exchange: string;
  last: number | null;
  prevClose: number | null;
  change: number;
  changePct: number;
  loading: boolean;
  error?: string;
}

interface CandleResp {
  count: number;
  candles: { ts: string; open: number; high: number; low: number; close: number; volume: number }[];
}

interface UseQuotesParams {
  symbols: { symbol: string; exchange: string }[];
  /** Auto-refresh interval in ms. 0 = no auto-refresh. */
  refreshMs?: number;
}

export function useQuotes({ symbols, refreshMs = 0 }: UseQuotesParams) {
  const [quotes, setQuotes] = useState<Record<string, Quote>>({});
  const inflight = useRef<Set<string>>(new Set());

  const fetchOne = useCallback(async (sym: { symbol: string; exchange: string }) => {
    const key = `${sym.symbol}:${sym.exchange}`;
    if (inflight.current.has(key)) return;
    inflight.current.add(key);

    setQuotes((cur) => {
      const existing = cur[key];
      return {
        ...cur,
        [key]: existing
          ? { ...existing, loading: true }
          : { ...sym, last: null, prevClose: null, change: 0, changePct: 0, loading: true },
      };
    });

    try {
      // 5 days is enough to find the previous trading day even over a weekend
      const resp = await api.get<CandleResp>("/candles", {
        params: { symbol: sym.symbol, exchange: sym.exchange, timeframe: "1d", days: 5 },
      });
      const c = resp.data.candles;
      if (!c.length) {
        setQuotes((cur) => ({
          ...cur,
          [key]: { ...sym, last: null, prevClose: null, change: 0, changePct: 0, loading: false, error: "no data" },
        }));
        return;
      }
      const last = c[c.length - 1].close;
      const prev = c.length > 1 ? c[c.length - 2].close : null;
      const change = prev !== null ? last - prev : 0;
      const changePct = prev !== null && prev !== 0 ? (change / prev) * 100 : 0;
      setQuotes((cur) => ({
        ...cur,
        [key]: { ...sym, last, prevClose: prev, change, changePct, loading: false },
      }));
    } catch (e) {
      setQuotes((cur) => ({
        ...cur,
        [key]: { ...sym, last: null, prevClose: null, change: 0, changePct: 0, loading: false, error: (e as Error).message },
      }));
    } finally {
      inflight.current.delete(key);
    }
  }, []);

  const refetchAll = useCallback(() => {
    symbols.forEach(fetchOne);
  }, [symbols, fetchOne]);

  // Trigger initial + on-symbol-change fetches
  useEffect(() => {
    symbols.forEach((s) => {
      const key = `${s.symbol}:${s.exchange}`;
      if (!quotes[key]) fetchOne(s);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbols.map((s) => `${s.symbol}:${s.exchange}`).join("|")]);

  // Auto-refresh
  useEffect(() => {
    if (!refreshMs) return;
    const id = setInterval(refetchAll, refreshMs);
    return () => clearInterval(id);
  }, [refreshMs, refetchAll]);

  return { quotes, refetch: refetchAll };
}
